import { useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Line, Pie } from "react-chartjs-2";
import { Tooltip as HeouiTooltip } from "@heroui/react";
import {
  ChartBar,
  ChartLine,
  ChartPie,
  CaretDown,
  CaretRight,
  Sparkle,
} from "@phosphor-icons/react";
import { TicketInternalStatus } from "@/shared/types/helpdesk";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
);

interface AnalyticsData {
  type:
    | "label-frequency"
    | "by-building"
    | "by-unit"
    | "by-reporter"
    | "time-series"
    | "filtered-list"
    | "table"
    | "ticket-analysis"
    | "image-list"
    | "document-list"
    | "validity-check";
  title: string;
  data: any[];
  chartType?: "bar" | "line" | "pie" | "table";
  totalCount?: number; // For filtered-list and by-reporter results
  tickets?: any[]; // For time-series: array of { period, tickets[] }
  fileLogs?: any[]; // For image-list and document-list: array of FileLog objects
  message?: string; // Message from backend
  isValid?: boolean; // For validity-check
  fileLog?: any; // For validity-check: single FileLog
  reason?: string; // For validity-check: reason
}

interface AnalyticsVisualizationProps {
  analyticsData: AnalyticsData;
  onAnalyzeTicket?: (ticketId: number) => void;
  onAnalyzeDocument?: (documentId: number, documentLabel?: string[]) => void;
  onDrillDown?: (filters: {
    label?: string;
    buildingId?: string;
    unitId?: string;
  }) => void;
}

/**
 * Parse date string from backend
 * Handles formats: 'yyyy-MM-dd', 'YYYY-W##', 'yyyy-MM'
 */
const parseDateString = (dateStr: string): Date => {
  // Week format: '2024-W03'
  if (dateStr.includes("-W")) {
    const [year, weekStr] = dateStr.split("-W");
    const weekNumber = parseInt(weekStr, 10);

    // Calculate date of the Monday of that week
    const jan4 = new Date(parseInt(year), 0, 4);
    const jan4Day = jan4.getDay() || 7; // Sunday = 7
    const mondayOfWeek1 = new Date(
      jan4.getTime() - (jan4Day - 1) * 24 * 60 * 60 * 1000,
    );
    const targetDate = new Date(
      mondayOfWeek1.getTime() + (weekNumber - 1) * 7 * 24 * 60 * 60 * 1000,
    );

    return targetDate;
  }

  // Month format: '2024-01' -> '2024-01-01'
  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    return new Date(`${dateStr}-01`);
  }

  // Day format: '2024-01-15' (standard ISO format)
  return new Date(dateStr);
};

/**
 * Format date for display based on the original format
 */
const formatDateForDisplay = (dateStr: string, date: Date): string => {
  // Week format: show as "Week 3, 2024"
  if (dateStr.includes("-W")) {
    const [year, weekStr] = dateStr.split("-W");
    return `Week ${parseInt(weekStr, 10)}, ${year}`;
  }

  // Month format: show as "jan 2024"
  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    return date.toLocaleDateString("nl-BE", {
      month: "short",
      year: "numeric",
    });
  }

  // Day format: show as "15 jan 2024"
  return date.toLocaleDateString("nl-BE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/**
 * AnalyticsVisualization Component
 *
 * Toont analytics data in verschillende formats:
 * - Bar charts voor label frequentie, gebouw/unit verdeling
 * - Line charts voor tijd series (evolutie over tijd)
 * - Pie charts voor verdelingen
 * - Tabellen voor gedetailleerde data
 */
export const AnalyticsVisualization: React.FC<AnalyticsVisualizationProps> = ({
  analyticsData,
  onAnalyzeTicket,
  onAnalyzeDocument,
  onDrillDown,
}) => {
  // State for expanded periods in time-series drill-down
  const [expandedPeriods, setExpandedPeriods] = useState<Set<string>>(
    new Set(),
  );

  // Debug logging
  console.log("AnalyticsVisualization received:", analyticsData);

  // Toggle period expansion
  const togglePeriod = (period: string) => {
    setExpandedPeriods((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(period)) {
        newSet.delete(period);
      } else {
        newSet.add(period);
      }
      return newSet;
    });
  };

  const getChartData = () => {
    const { type, data } = analyticsData;
    console.log("Getting chart data for type:", type, "data:", data);

    // Defensive check: ensure data is an array
    if (!data || !Array.isArray(data)) {
      console.error("❌ Data is not an array:", data);
      return null;
    }

    if (data.length === 0) {
      console.warn("⚠️ Data array is empty");
      return null;
    }

    switch (type) {
      case "label-frequency":
        return {
          labels: data.map((item) => item.label || item._id),
          datasets: [
            {
              label: "Aantal Tickets",
              data: data.map((item) => item.count),
              backgroundColor: "rgba(234, 129, 98, 0.7)",
              borderColor: "#EA8162",
              borderWidth: 2,
            },
          ],
        };

      case "by-building":
        return {
          labels: data.map((item) => item.buildingId || item._id),
          datasets: [
            {
              label: "Aantal Tickets",
              data: data.map((item) => item.count),
              backgroundColor: "rgba(91, 115, 4, 0.7)",
              borderColor: "#5B7304",
              borderWidth: 2,
            },
          ],
        };

      case "by-unit":
        return {
          labels: data.map((item) => item.unitId || item._id),
          datasets: [
            {
              label: "Aantal Tickets",
              data: data.map((item) => item.count),
              backgroundColor: "rgba(49, 64, 59, 0.7)",
              borderColor: "#31403B",
              borderWidth: 2,
            },
          ],
        };

      case "by-reporter":
        return {
          labels: data.map((item) => item.reporter?.name || "Onbekend"),
          datasets: [
            {
              label: "Aantal Tickets",
              data: data.map((item) => item.ticketCount),
              backgroundColor: "rgba(234, 129, 98, 0.7)",
              borderColor: "#EA8162",
              borderWidth: 2,
            },
          ],
        };

      case "time-series":
        return {
          labels: data.map((item) => {
            const dateStr = item.date || item._id;
            const date = parseDateString(dateStr);
            return formatDateForDisplay(dateStr, date);
          }),
          datasets: [
            {
              label: "Aantal Open Tickets",
              data: data.map((item) => item.count),
              backgroundColor: "rgba(234, 129, 98, 0.2)",
              borderColor: "#EA8162",
              borderWidth: 2,
              fill: true,
              tension: 0.4,
            },
          ],
        };

      case "filtered-list":
        // For filtered-list, aggregate tickets by building to create chart
        const buildingCounts = new Map<string, number>();
        data.forEach((ticket: any) => {
          const buildingId = ticket.building?.id || "Onbekend";
          buildingCounts.set(
            buildingId,
            (buildingCounts.get(buildingId) || 0) + 1,
          );
        });

        // Sort by count descending
        const sortedBuildings = Array.from(buildingCounts.entries()).sort(
          (a, b) => b[1] - a[1],
        );

        return {
          labels: sortedBuildings.map(([buildingId]) => buildingId),
          datasets: [
            {
              label: "Aantal Tickets",
              data: sortedBuildings.map(([, count]) => count),
              backgroundColor: "rgba(91, 115, 4, 0.7)",
              borderColor: "#5B7304",
              borderWidth: 2,
            },
          ],
        };

      default:
        return null;
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#31403B",
        titleColor: "#F9F0EE",
        bodyColor: "#F9F0EE",
        borderColor: "#EA8162",
        borderWidth: 1,
        padding: 12,
        displayColors: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: "#838C89",
          font: {
            size: 11,
          },
          precision: 0,
        },
        grid: {
          color: "rgba(49, 64, 59, 0.1)",
        },
      },
      x: {
        ticks: {
          color: "#31403B",
          font: {
            size: 11,
          },
        },
        grid: {
          display: false,
        },
      },
    },
  };

  const renderChart = () => {
    // Skip chart for ticket analysis - only show table
    if (analyticsData.type === "ticket-analysis") {
      return null;
    }

    const chartData = getChartData();
    if (!chartData) {
      console.error("getChartData returned null for type:", analyticsData.type);
      return null;
    }

    const { chartType = "bar" } = analyticsData;
    console.log("Rendering chart type:", chartType);

    if (chartType === "table") {
      return renderTable();
    }

    if (chartType === "pie") {
      return (
        <div className="flex h-80 items-center justify-center">
          <div className="w-80">
            <Pie
              data={chartData}
              options={{ ...chartOptions, scales: undefined }}
            />
          </div>
        </div>
      );
    }

    if (chartType === "line" || analyticsData.type === "time-series") {
      return (
        <div className="h-80">
          <Line data={chartData} options={chartOptions} />
        </div>
      );
    }

    // Default: bar chart
    return (
      <div className="h-80">
        <Bar data={chartData} options={chartOptions} />
      </div>
    );
  };

  const renderTable = () => {
    const { data, type, totalCount, tickets } = analyticsData;

    // Debug logging for time-series
    if (type === "time-series") {
      console.log("🎯 Time-series table rendering:", {
        hasTickets: !!tickets,
        isArray: Array.isArray(tickets),
        ticketsLength: tickets?.length,
        tickets: tickets,
      });
    }

    // Special rendering for ticket-analysis (single ticket status)
    if (
      type === "ticket-analysis" &&
      data &&
      typeof data === "object" &&
      !Array.isArray(data)
    ) {
      const analysis: any = data;

      return (
        <div className="space-y-4">
          {/* Ticket Header */}
          <div className="from-moss-primary/5 to-brick-primary/5 border-neutral-primary/10 rounded-lg border bg-gradient-to-r p-4">
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-neutral-primary text-base font-semibold">
                Ticket H{analysis.ticketId}: {analysis.title}
              </h4>
              <span
                className={`inline-flex items-center rounded px-2 py-1 text-xs font-medium ${
                  analysis.internalStatus === "Gesloten"
                    ? "bg-neutral-primary/10 text-neutral-primary"
                    : "bg-moss-primary/10 text-moss-primary"
                }`}
              >
                {analysis.internalStatus || TicketInternalStatus.NEW_UNANSWERED}
              </span>
            </div>
            {analysis.label && (
              <div>
                <span className="bg-brick-primary/10 text-brick-primary inline-flex items-center rounded px-2 py-1 text-xs font-medium">
                  {analysis.label}
                </span>
              </div>
            )}
          </div>

          {/* Critical Information Panel */}
          <div className="border-brick-primary/30 bg-brick-primary/5 overflow-hidden rounded-lg border-2">
            <div className="bg-brick-primary/10 border-brick-primary/20 border-b px-4 py-2">
              <h5 className="text-brick-primary flex items-center gap-2 text-sm font-semibold">
                <span>⚠️</span>
                <span>Kritische Informatie</span>
              </h5>
            </div>
            <div className="space-y-3 p-4">
              {/* Problem Description */}
              {analysis.issueDescription && (
                <div>
                  <h6 className="text-neutral-secondaryMid mb-1 text-xs font-semibold">
                    🔍 Wat is het probleem?
                  </h6>
                  <div className="text-neutral-primary border-neutral-primary/10 rounded border bg-white/50 p-3 text-sm whitespace-pre-line">
                    {analysis.issueDescription}
                  </div>
                </div>
              )}

              {/* Waiting Time */}
              <div className="grid grid-cols-2 gap-3">
                <div className="border-neutral-primary/10 rounded border bg-white/50 p-3">
                  <h6 className="text-neutral-secondaryMid mb-1 text-xs font-semibold">
                    ⏱️ Wachttijd
                  </h6>
                  <p
                    className={`text-lg font-bold ${
                      analysis.waitingDays > 7
                        ? "text-brick-primary"
                        : analysis.waitingDays > 3
                          ? "text-orange-600"
                          : "text-moss-primary"
                    }`}
                  >
                    {analysis.waitingDays}{" "}
                    {analysis.waitingDays === 1 ? "dag" : "dagen"}
                  </p>
                  <p className="text-neutral-secondaryMid text-xs">
                    sinds laatste activiteit
                  </p>
                </div>

                {/* Last Message From */}
                <div className="border-neutral-primary/10 rounded border bg-white/50 p-3">
                  <h6 className="text-neutral-secondaryMid mb-1 text-xs font-semibold">
                    💬 Laatste bericht van
                  </h6>
                  <p className="text-neutral-primary truncate text-sm font-semibold">
                    {analysis.lastMessageFrom}
                  </p>
                  <p className="text-neutral-secondaryMid text-xs">
                    {new Date(analysis.lastUpdate).toLocaleDateString("nl-BE")}
                  </p>
                </div>
              </div>

              {/* Actions Taken & Supplier */}
              <div className="grid grid-cols-2 gap-3">
                <div className="border-neutral-primary/10 rounded border bg-white/50 p-3">
                  <h6 className="text-neutral-secondaryMid mb-1 text-xs font-semibold">
                    ✅ Acties ondernomen?
                  </h6>
                  <p
                    className={`text-sm font-semibold ${analysis.actionsTaken ? "text-moss-primary" : "text-brick-primary"}`}
                  >
                    {analysis.actionsTaken ? "Ja" : "Nee"}
                  </p>
                  {analysis.actionsTaken && (
                    <p className="text-neutral-secondaryMid text-xs">
                      {analysis.emailCount} email(s),{" "}
                      {analysis.actionNotesCount} notitie(s)
                    </p>
                  )}
                </div>

                <div className="border-neutral-primary/10 rounded border bg-white/50 p-3">
                  <h6 className="text-neutral-secondaryMid mb-1 text-xs font-semibold">
                    🏢 Leverancier
                  </h6>
                  <p
                    className={`truncate text-sm font-semibold ${analysis.assignedSupplier ? "text-moss-primary" : "text-brick-primary"}`}
                  >
                    {analysis.assignedSupplier || "Niet toegewezen"}
                  </p>
                  {analysis.assignedSupplier && (
                    <p className="text-neutral-secondaryMid text-xs">
                      Toegewezen
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Email analysis */}
          {analysis.emailAnalysis && (
            <div className="border-neutral-primary/10 overflow-hidden rounded-lg border">
              <div className="bg-neutral-primary/5 border-neutral-primary/10 border-b px-4 py-2">
                <h5 className="text-neutral-primary text-sm font-semibold">
                  📧 Email Thread Analyse
                </h5>
              </div>
              <div className="p-4">
                <p className="text-neutral-primary mb-3 text-sm">
                  {analysis.emailAnalysis.status}
                </p>

                {analysis.emailAnalysis.latestEmail && (
                  <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <p className="text-neutral-primary mb-1 text-xs font-semibold">
                      Laatste Email:
                    </p>
                    <p className="text-neutral-secondaryMid mb-1 text-xs">
                      Van: {analysis.emailAnalysis.latestEmail.from}
                    </p>
                    <p className="text-neutral-secondaryMid mb-1 text-xs">
                      Datum:{" "}
                      {new Date(
                        analysis.emailAnalysis.latestEmail.date,
                      ).toLocaleString("nl-BE")}
                    </p>
                    <p className="text-neutral-secondaryMid mb-1 text-xs">
                      Onderwerp: {analysis.emailAnalysis.latestEmail.subject}
                    </p>
                    <p className="text-neutral-primary mt-2 text-xs italic">
                      "{analysis.emailAnalysis.latestEmail.snippet}..."
                    </p>
                  </div>
                )}

                {analysis.emailAnalysis.statistics && (
                  <div className="text-neutral-secondaryMid flex gap-4 text-xs">
                    <span>
                      Totaal: {analysis.emailAnalysis.statistics.total}
                    </span>
                    <span>
                      Intern: {analysis.emailAnalysis.statistics.internal}
                    </span>
                    <span>
                      Extern: {analysis.emailAnalysis.statistics.external}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action notes analysis */}
          {analysis.actionNotesAnalysis && (
            <div className="border-neutral-primary/10 overflow-hidden rounded-lg border">
              <div className="bg-neutral-primary/5 border-neutral-primary/10 border-b px-4 py-2">
                <h5 className="text-neutral-primary text-sm font-semibold">
                  📝 Actie Notities Analyse
                </h5>
              </div>
              <div className="p-4">
                <p className="text-neutral-primary mb-3 text-sm">
                  {analysis.actionNotesAnalysis.status}
                </p>

                {analysis.actionNotesAnalysis.latestNote && (
                  <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                    <p className="text-neutral-primary mb-1 text-xs font-semibold">
                      Laatste Notitie:
                    </p>
                    <p className="text-neutral-secondaryMid mb-1 text-xs">
                      Door: {analysis.actionNotesAnalysis.latestNote.createdBy}
                    </p>
                    <p className="text-neutral-secondaryMid mb-1 text-xs">
                      Datum:{" "}
                      {new Date(
                        analysis.actionNotesAnalysis.latestNote.date,
                      ).toLocaleString("nl-BE")}
                    </p>
                    <p className="text-neutral-primary mt-2 text-xs italic">
                      "{analysis.actionNotesAnalysis.latestNote.content}"
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Attachments/Images */}
          {analysis.attachments &&
            (analysis.attachments.images.length > 0 ||
              analysis.attachments.documents.length > 0) && (
              <div className="border-neutral-primary/10 overflow-hidden rounded-lg border">
                <div className="bg-neutral-primary/5 border-neutral-primary/10 border-b px-4 py-2">
                  <h5 className="text-neutral-primary text-sm font-semibold">
                    📎 Bijlagen ({analysis.attachmentsCount})
                  </h5>
                </div>
                <div className="p-4">
                  {/* Images */}
                  {analysis.attachments.images.length > 0 && (
                    <div className="mb-4">
                      <p className="text-neutral-secondaryMid mb-2 text-xs font-semibold">
                        Afbeeldingen ({analysis.attachments.images.length}):
                      </p>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                        {analysis.attachments.images
                          .slice(0, 6)
                          .map((img: any, idx: number) => (
                            <div key={idx} className="group relative">
                              <img
                                src={img.url}
                                alt={img.name}
                                className="border-neutral-primary/10 h-32 w-full cursor-pointer rounded border object-cover transition-opacity hover:opacity-90"
                                onClick={() => window.open(img.url, "_blank")}
                              />
                              <div className="absolute right-0 bottom-0 left-0 truncate bg-black/50 p-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                                {img.name}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Documents */}
                  {analysis.attachments.documents.length > 0 && (
                    <div>
                      <p className="text-neutral-secondaryMid mb-2 text-xs font-semibold">
                        Documenten ({analysis.attachments.documents.length}):
                      </p>
                      <div className="space-y-1">
                        {analysis.attachments.documents.map(
                          (doc: any, idx: number) => (
                            <a
                              key={idx}
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-moss-primary hover:text-moss-primary/80 flex items-center gap-2 text-xs transition-colors"
                            >
                              <span>📄</span>
                              <span className="truncate">{doc.name}</span>
                            </a>
                          ),
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* Proposed Solutions from conversation */}
          {analysis.proposedSolutions &&
            analysis.proposedSolutions.length > 0 && (
              <div className="border-neutral-primary/10 overflow-hidden rounded-lg border">
                <div className="bg-neutral-primary/5 border-neutral-primary/10 border-b px-4 py-2">
                  <h5 className="text-neutral-primary text-sm font-semibold">
                    💡 Voorgestelde Oplossingen uit Correspondentie
                  </h5>
                </div>
                <div className="p-4">
                  <ul className="space-y-2">
                    {analysis.proposedSolutions.map(
                      (solution: string, idx: number) => (
                        <li
                          key={idx}
                          className="text-neutral-primary flex gap-2 text-sm"
                        >
                          <span className="text-moss-primary">•</span>
                          <span className="flex-1">{solution}</span>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              </div>
            )}

          {/* AI Solution Suggestions */}
          {analysis.solutionSuggestions &&
            analysis.solutionSuggestions.length > 0 && (
              <div className="border-moss-primary/30 bg-moss-primary/5 overflow-hidden rounded-lg border">
                <div className="bg-moss-primary/10 border-moss-primary/20 border-b px-4 py-2">
                  <h5 className="text-moss-primary text-sm font-semibold">
                    ✨ Mogelijke Oplossingen & Aanbevelingen
                  </h5>
                </div>
                <div className="p-4">
                  <ul className="space-y-2">
                    {analysis.solutionSuggestions.map(
                      (suggestion: string, idx: number) => (
                        <li
                          key={idx}
                          className="text-neutral-primary flex gap-2 text-sm"
                        >
                          <span className="text-moss-primary font-bold">
                            {idx + 1}.
                          </span>
                          <span className="flex-1">{suggestion}</span>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              </div>
            )}
        </div>
      );
    }

    // Defensive check
    if (!data || !Array.isArray(data) || data.length === 0) {
      return null;
    }

    // Special rendering for time-series (show period summary + underlying tickets with drill-down)
    if (type === "time-series" && tickets && Array.isArray(tickets)) {
      console.log("✅ Rendering time-series with tickets details");

      return (
        <div className="space-y-3">
          <div className="text-neutral-secondaryMid mb-2 text-sm">
            {totalCount !== undefined ? `${totalCount} tickets totaal` : ""}
          </div>
          {tickets.length === 0 && (
            <div className="text-neutral-secondaryMid py-4 text-center">
              Geen ticket details beschikbaar voor deze periode
            </div>
          )}
          {tickets.map((periodData, index) => {
            const period = periodData.period;
            const periodTickets = periodData.tickets || [];

            if (periodTickets.length === 0) return null; // Skip periods with no tickets

            const dateStr = period;
            const date = parseDateString(dateStr);
            const displayDate = formatDateForDisplay(dateStr, date);
            const isExpanded = expandedPeriods.has(period);

            return (
              <div
                key={index}
                className="border-neutral-primary/10 overflow-hidden rounded-lg border"
              >
                {/* Period summary header - clickable to expand/collapse */}
                <button
                  onClick={() => togglePeriod(period)}
                  className="bg-neutral-primary/5 hover:bg-neutral-primary/10 w-full px-4 py-3 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isExpanded ? (
                        <CaretDown
                          className="text-neutral-primary h-4 w-4"
                          weight="bold"
                        />
                      ) : (
                        <CaretRight
                          className="text-neutral-primary h-4 w-4"
                          weight="bold"
                        />
                      )}
                      <div className="text-neutral-primary font-semibold">
                        {displayDate}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-brick-primary text-sm font-semibold">
                        {periodTickets.length}{" "}
                        {periodTickets.length === 1
                          ? "open ticket"
                          : "open tickets"}
                      </div>
                    </div>
                  </div>
                </button>

                {/* Tickets table - only shown when expanded */}
                {isExpanded && (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[800px] text-sm">
                      <thead className="bg-neutral-primary/5 border-neutral-primary/10 border-t">
                        <tr>
                          <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                            ID
                          </th>
                          <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                            Titel
                          </th>
                          <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                            Label
                          </th>
                          <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                            Gebouw
                          </th>
                          <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                            Status
                          </th>
                          <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                            Acties
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {periodTickets.map((ticket: any) => (
                          <tr
                            key={ticket.id}
                            className="border-neutral-primary/10 hover:bg-neutral-primary/5 border-b"
                          >
                            <td className="text-neutral-primary px-4 py-2 font-mono text-xs">
                              H{ticket.id}
                            </td>
                            <td
                              className="text-neutral-primary hover:text-moss-primary max-w-xs cursor-pointer truncate px-4 py-2"
                              onClick={() =>
                                window.open(`/helpdesk/${ticket.id}`, "_blank")
                              }
                            >
                              {ticket.title || "-"}
                            </td>
                            <td className="px-4 py-2">
                              <span className="bg-brick-primary/10 text-brick-primary inline-flex items-center rounded px-2 py-1 text-xs font-medium">
                                {ticket.label || "-"}
                              </span>
                            </td>
                            <td className="text-neutral-primary px-4 py-2 text-xs">
                              {ticket.building?.id || "-"}
                            </td>
                            <td className="px-4 py-2">
                              <span
                                className={`inline-flex items-center rounded px-2 py-1 text-xs font-medium ${
                                  ticket.internalStatus === "Gesloten"
                                    ? "bg-neutral-primary/10 text-neutral-primary"
                                    : "bg-moss-primary/10 text-moss-primary"
                                }`}
                              >
                                {ticket.internalStatus || "Ontvangen"}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAnalyzeTicket?.(ticket.id);
                                }}
                                className="hover:bg-moss-primary/10 cursor-pointer rounded p-1 transition-colors"
                                title={`Analyseer ticket H${ticket.id}`}
                              >
                                <Sparkle
                                  className="text-moss-primary h-4 w-4"
                                  weight="fill"
                                />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // Special rendering for by-reporter (show reporter summary + underlying tickets)
    if (type === "by-reporter") {
      return (
        <div className="space-y-6">
          <div className="text-neutral-secondaryMid mb-2 text-sm">
            {totalCount !== undefined
              ? `${totalCount} tickets totaal`
              : `${data.reduce((sum, item) => sum + item.ticketCount, 0)} tickets`}
          </div>
          {data.map((reporterStat, index) => (
            <div
              key={index}
              className="border-neutral-primary/10 overflow-hidden rounded-lg border"
            >
              {/* Reporter summary header */}
              <div className="bg-neutral-primary/5 border-neutral-primary/10 border-b px-4 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-neutral-primary font-semibold">
                      {reporterStat.reporter?.name || "Onbekend"}
                    </div>
                    {reporterStat.reporter?.email && (
                      <div className="text-neutral-secondaryMid text-xs">
                        {reporterStat.reporter.email}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-brick-primary text-sm font-semibold">
                      {reporterStat.ticketCount}{" "}
                      {reporterStat.ticketCount === 1 ? "ticket" : "tickets"}
                    </div>
                  </div>
                </div>
              </div>
              {/* Tickets table */}
              <table className="w-full text-sm">
                <thead className="bg-neutral-primary/5">
                  <tr>
                    <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                      ID
                    </th>
                    <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                      Titel
                    </th>
                    <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                      Label
                    </th>
                    <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                      Gebouw
                    </th>
                    <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reporterStat.tickets.map((ticket: any) => (
                    <tr
                      key={ticket.id}
                      className="border-neutral-primary/10 hover:bg-neutral-primary/5 cursor-pointer border-b"
                      onClick={() =>
                        window.open(`/helpdesk/${ticket.id}`, "_blank")
                      }
                    >
                      <td className="text-neutral-primary px-4 py-2 font-mono text-xs">
                        H{ticket.id}
                      </td>
                      <td className="text-neutral-primary max-w-xs truncate px-4 py-2">
                        {ticket.title ||
                          ticket.description?.substring(0, 60) ||
                          "-"}
                      </td>
                      <td className="px-4 py-2">
                        <span className="bg-brick-primary/10 text-brick-primary inline-flex items-center rounded px-2 py-1 text-xs font-medium">
                          {ticket.label || "-"}
                        </span>
                      </td>
                      <td className="text-neutral-primary px-4 py-2 text-xs">
                        {ticket.building?.id || "-"}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-flex items-center rounded px-2 py-1 text-xs font-medium ${
                            ticket.internalStatus === "Gesloten"
                              ? "bg-neutral-primary/10 text-neutral-primary"
                              : "bg-moss-primary/10 text-moss-primary"
                          }`}
                        >
                          {ticket.internalStatus ||
                            TicketInternalStatus.NEW_UNANSWERED}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      );
    }

    // Special rendering for filtered-list (actual ticket objects)
    if (type === "filtered-list") {
      return (
        <div className="overflow-x-auto">
          <div className="text-neutral-secondaryMid mb-2 text-sm">
            {totalCount !== undefined
              ? `${totalCount} tickets gevonden`
              : `${data.length} tickets`}
          </div>
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-neutral-primary/5 border-neutral-primary/10 border-b">
              <tr>
                <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                  ID
                </th>
                <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                  Titel
                </th>
                <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                  Label
                </th>
                <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                  Gebouw
                </th>
                <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                  Unit
                </th>
                <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                  Status
                </th>
                <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((ticket, index) => (
                <tr
                  key={ticket.id || index}
                  className="border-neutral-primary/10 hover:bg-neutral-primary/5 border-b"
                >
                  <td className="text-neutral-primary px-4 py-2 font-mono text-xs">
                    H{ticket.id}
                  </td>
                  <td
                    className="text-neutral-primary hover:text-moss-primary max-w-xs cursor-pointer truncate px-4 py-2"
                    onClick={() =>
                      window.open(`/helpdesk/${ticket.id}`, "_blank")
                    }
                  >
                    {ticket.title ||
                      ticket.description?.substring(0, 60) ||
                      "-"}
                  </td>
                  <td className="px-4 py-2">
                    <span className="bg-brick-primary/10 text-brick-primary inline-flex items-center rounded px-2 py-1 text-xs font-medium">
                      {ticket.label || "-"}
                    </span>
                  </td>
                  <td className="text-neutral-primary px-4 py-2 text-xs">
                    {ticket.building?.id || "-"}
                  </td>
                  <td className="text-neutral-primary px-4 py-2 text-xs">
                    {ticket.unit?.boxNumber || "-"}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex items-center rounded px-2 py-1 text-xs font-medium ${
                        ticket.internalStatus === "Gesloten"
                          ? "bg-neutral-primary/10 text-neutral-primary"
                          : "bg-moss-primary/10 text-moss-primary"
                      }`}
                    >
                      {ticket.internalStatus ||
                        TicketInternalStatus.NEW_UNANSWERED}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAnalyzeTicket?.(ticket.id);
                      }}
                      className="hover:bg-moss-primary/10 cursor-pointer rounded p-1 transition-colors"
                      title={`Analyseer ticket H${ticket.id}`}
                    >
                      <Sparkle
                        className="text-moss-primary h-4 w-4"
                        weight="fill"
                      />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Original aggregate data table rendering
    // Add warning for time-series without ticket details
    if (type === "time-series") {
      console.warn(
        "⚠️ Time-series rendering fallback: tickets data missing or invalid",
      );
    }

    return (
      <div className="overflow-x-auto">
        {type === "time-series" && (
          <div className="mb-3 rounded border border-yellow-200 bg-yellow-50 p-2 text-xs text-yellow-800">
            ⚠️ Ticket details niet beschikbaar - alleen samenvatting wordt
            getoond
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-neutral-primary/5 border-neutral-primary/10 border-b">
            <tr>
              {type === "label-frequency" && (
                <>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    Label
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-right font-semibold">
                    Aantal
                  </th>
                </>
              )}
              {type === "by-building" && (
                <>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    Gebouw ID
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-right font-semibold">
                    Aantal
                  </th>
                </>
              )}
              {type === "by-unit" && (
                <>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    Unit ID
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-right font-semibold">
                    Aantal
                  </th>
                </>
              )}
              {type === "time-series" && (
                <>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    Datum
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-right font-semibold">
                    Aantal
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => {
              // Determine drill-down filters based on type
              const getDrillDownFilters = () => {
                if (type === "label-frequency") {
                  return { label: item.label || item._id };
                } else if (type === "by-building") {
                  return { buildingId: item.buildingId || item._id };
                } else if (type === "by-unit") {
                  return { unitId: item.unitId || item._id };
                }
                return {};
              };

              const canDrillDown =
                type === "label-frequency" ||
                type === "by-building" ||
                type === "by-unit";

              return (
                <tr
                  key={index}
                  onClick={() =>
                    canDrillDown && onDrillDown?.(getDrillDownFilters())
                  }
                  className={`border-neutral-primary/10 border-b ${
                    canDrillDown
                      ? "hover:bg-moss-primary/10 cursor-pointer transition-colors"
                      : "hover:bg-neutral-primary/5"
                  }`}
                  title={
                    canDrillDown ? "Klik om tickets te bekijken" : undefined
                  }
                >
                  <td className="text-neutral-primary px-4 py-2">
                    {type === "time-series"
                      ? (() => {
                          const dateStr = item.date || item._id;
                          const date = parseDateString(dateStr);
                          return formatDateForDisplay(dateStr, date);
                        })()
                      : item.label ||
                        item.buildingId ||
                        item.unitId ||
                        item._id}
                  </td>
                  <td className="text-neutral-primary px-4 py-2 text-right font-semibold">
                    {item.count}
                    {canDrillDown && (
                      <span className="text-moss-primary ml-2 text-xs">→</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const getChartIcon = () => {
    const { chartType = "bar" } = analyticsData;
    switch (chartType) {
      case "line":
        return (
          <ChartLine className="text-brick-primary h-5 w-5" weight="fill" />
        );
      case "pie":
        return (
          <ChartPie className="text-brick-primary h-5 w-5" weight="fill" />
        );
      default:
        return (
          <ChartBar className="text-brick-primary h-5 w-5" weight="fill" />
        );
    }
  };

  // Special rendering for image-list
  if (analyticsData.type === "image-list") {
    const images = analyticsData.fileLogs || analyticsData.data || [];

    return (
      <div className="border-moss-primary/30 rounded-lg border bg-white p-4">
        <div className="mb-4">
          <h3 className="text-neutral-primary mb-2 text-base font-semibold">
            {analyticsData.message ||
              `${images.length} afbeelding(en) gevonden`}
          </h3>
        </div>

        {images.length === 0 ? (
          <div className="text-neutral-secondaryMid py-8 text-center">
            Geen afbeeldingen gevonden
          </div>
        ) : (
          <div className="space-y-4">
            {/* Image grid */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {images.map((fileLog: any, idx: number) => (
                <div key={fileLog.id || idx} className="group relative">
                  <a
                    href={fileLog.imageUrl || fileLog.sharePointFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <img
                      src={fileLog.imageUrl || fileLog.sharePointFileUrl}
                      alt={fileLog.label?.join(", ") || "Afbeelding"}
                      className="border-neutral-primary/10 h-40 w-full cursor-pointer rounded-lg border object-cover transition-opacity hover:opacity-90"
                    />
                    <div className="absolute right-0 bottom-0 left-0 rounded-b-lg bg-black/70 p-2 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                      <p className="truncate font-medium">
                        {fileLog.label?.join(", ") || "Afbeelding"}
                      </p>
                      {fileLog.building && (
                        <p className="truncate text-xs text-gray-300">
                          {fileLog.building.id}
                        </p>
                      )}
                    </div>
                  </a>
                </div>
              ))}
            </div>

            {/* Detailed table below images */}
            <div className="border-neutral-primary/10 border-t pt-4">
              <h4 className="text-neutral-primary mb-3 text-sm font-semibold">
                Details
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-primary/5 border-neutral-primary/10 border-b">
                    <tr>
                      <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                        Label
                      </th>
                      <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                        Gebouw
                      </th>
                      <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                        Status
                      </th>
                      <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                        Datum
                      </th>
                      <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                        Acties
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {images.map((fileLog: any) => (
                      <tr
                        key={fileLog.id}
                        className="border-neutral-primary/10 hover:bg-neutral-primary/5 border-b"
                      >
                        <td className="px-4 py-2">
                          {fileLog.label?.map((label: string, idx: number) => (
                            <span
                              key={idx}
                              className="bg-moss-primary/10 text-moss-primary mr-1 mb-1 inline-flex items-center rounded px-2 py-1 text-xs font-medium"
                            >
                              {label}
                            </span>
                          ))}
                        </td>
                        <td className="text-neutral-primary px-4 py-2 text-xs">
                          {fileLog.building?.id ||
                            fileLog.unit?.building?.id ||
                            "-"}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-flex items-center rounded px-2 py-1 text-xs font-medium ${
                              fileLog.status === "OK" ||
                              fileLog.status === "Correct en volledig"
                                ? "bg-moss-primary/10 text-moss-primary"
                                : "bg-brick-primary/10 text-brick-primary"
                            }`}
                          >
                            {fileLog.status || "Onbekend"}
                          </span>
                        </td>
                        <td className="text-neutral-primary px-4 py-2 text-xs">
                          {fileLog.documentDate
                            ? new Date(fileLog.documentDate).toLocaleDateString(
                                "nl-BE",
                              )
                            : "-"}
                        </td>
                        <td className="px-4 py-2">
                          <a
                            href={fileLog.sharePointFileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-moss-primary hover:text-moss-primary/80 text-xs"
                          >
                            Open
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Special rendering for document-list
  if (analyticsData.type === "document-list") {
    const documents = analyticsData.fileLogs || analyticsData.data || [];

    return (
      <div className="border-moss-primary/30 rounded-lg border bg-white p-4">
        <div className="mb-4">
          <h3 className="text-neutral-primary mb-2 text-base font-semibold">
            {analyticsData.message ||
              `${documents.length} document(en) gevonden`}
          </h3>
        </div>

        {documents.length === 0 ? (
          <div className="text-neutral-secondaryMid py-8 text-center">
            Geen documenten gevonden
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-primary/5 border-neutral-primary/10 border-b">
                <tr>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    Label
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    Gebouw
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    Unit
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    Status
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    Document Datum
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    Vervaldatum
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    Acties
                  </th>
                </tr>
              </thead>
              <tbody>
                {documents.map((fileLog: any) => {
                  // Concatenate status description and summary
                  const statusText =
                    [fileLog.statusDescription, fileLog.summary]
                      .filter(Boolean)
                      .join(" - ") ||
                    fileLog.status ||
                    "Onbekend";

                  return (
                    <tr
                      key={fileLog.id}
                      className="border-neutral-primary/10 hover:bg-neutral-primary/5 border-b"
                    >
                      <td className="px-4 py-2">
                        {fileLog.label?.map((label: string, idx: number) => (
                          <span
                            key={idx}
                            className="bg-moss-primary/10 text-moss-primary mr-1 mb-1 inline-flex items-center rounded px-2 py-1 text-xs font-medium"
                          >
                            {label}
                          </span>
                        ))}
                      </td>
                      <td className="text-neutral-primary px-4 py-2 text-xs">
                        {fileLog.building?.id ||
                          fileLog.unit?.building?.id ||
                          "-"}
                      </td>
                      <td className="text-neutral-primary px-4 py-2 text-xs">
                        {fileLog.unit?.id || "-"}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-flex items-center rounded px-2 py-1 text-xs font-medium ${
                            fileLog.status === "OK" ||
                            fileLog.status === "Correct en volledig"
                              ? "bg-moss-primary/10 text-moss-primary"
                              : "bg-brick-primary/10 text-brick-primary"
                          }`}
                        >
                          {statusText}
                        </span>
                      </td>
                      <td className="text-neutral-primary px-4 py-2 text-xs">
                        {fileLog.documentDate
                          ? new Date(fileLog.documentDate).toLocaleDateString(
                              "nl-BE",
                            )
                          : "-"}
                      </td>
                      <td className="text-neutral-primary px-4 py-2 text-xs">
                        {fileLog.expirationDate
                          ? new Date(fileLog.expirationDate).toLocaleDateString(
                              "nl-BE",
                            )
                          : "-"}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <a
                            href={
                              fileLog.downloadUrl || fileLog.sharePointFileUrl
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-moss-primary hover:text-moss-primary/80 text-xs"
                          >
                            Download
                          </a>
                          <button
                            onClick={() =>
                              onAnalyzeDocument?.(fileLog.id, fileLog.label)
                            }
                            className="border-moss-primary/30 bg-moss-primary/5 text-moss-primary hover:bg-moss-primary/20 rounded-full border p-1 transition-colors"
                            title={`Analyseer document ${fileLog.id}`}
                          >
                            <Sparkle className="h-3 w-3" weight="fill" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // Special rendering for validity-check
  if (analyticsData.type === "validity-check") {
    const { isValid, fileLog, reason, message } = analyticsData;

    // Helper function to get status dot color (matching filesOverview)
    const getStatusDotColor = (status: string) => {
      switch (status) {
        case "OK":
        case "Correct en volledig":
          return "bg-green-500";
        case "OK met opmerkingen":
          return "bg-orange-500";
        case "Niet vereist":
          return "bg-green-500";
        case "Niet OK":
        case "Onbekend of na te kijken":
          return "bg-red-500";
        default:
          return "bg-gray-400";
      }
    };

    // Check if expiration date is expired
    const isExpired =
      fileLog?.expirationDate && new Date(fileLog.expirationDate) < new Date();

    return (
      <div className="border-moss-primary/30 rounded-lg border bg-white p-4">
        {/* Header with message */}
        <div
          className={`mb-4 rounded-lg border p-3 ${
            isValid
              ? "bg-moss-primary/10 border-moss-primary/30"
              : "bg-brick-primary/10 border-brick-primary/30"
          }`}
        >
          <p
            className={`font-semibold ${isValid ? "text-moss-primary" : "text-brick-primary"}`}
          >
            {message || reason}
          </p>
        </div>

        {fileLog ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-primary/5 border-neutral-primary/10 border-b">
                <tr>
                  <th className="text-neutral-primary px-4 py-2 text-center font-semibold">
                    Status
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    ID
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    Label
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    Gebouw
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    Unit
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    Document Datum
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-left font-semibold">
                    Vervaldatum
                  </th>
                  <th className="text-neutral-primary px-4 py-2 text-center font-semibold">
                    Acties
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-neutral-primary/10 hover:bg-neutral-primary/5 border-b">
                  {/* Status Dot */}
                  <td className="px-4 py-3 text-center">
                    <HeouiTooltip
                      content={
                        fileLog.statusDescription ||
                        fileLog.status ||
                        "Status onbekend"
                      }
                    >
                      <span
                        className={`border-neutral-primary/30 inline-block h-5 w-5 rounded-full border ${getStatusDotColor(fileLog.status)}`}
                      />
                    </HeouiTooltip>
                  </td>

                  {/* ID */}
                  <td className="text-neutral-primary px-4 py-3 font-mono text-xs font-semibold">
                    ID: {fileLog.id}
                  </td>

                  {/* Labels */}
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {fileLog.label?.map((label: string, idx: number) => (
                        <span
                          key={idx}
                          className="bg-moss-primary/10 text-moss-primary inline-flex items-center rounded px-2 py-1 text-xs font-medium"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  </td>

                  {/* Building */}
                  <td className="text-neutral-primary px-4 py-3 text-xs">
                    {fileLog.building?.id || "-"}
                  </td>

                  {/* Unit */}
                  <td className="text-neutral-primary px-4 py-3 text-xs">
                    {fileLog.unit?.id || "-"}
                  </td>

                  {/* Document Date */}
                  <td className="text-neutral-primary px-4 py-3 text-xs">
                    {fileLog.documentDate
                      ? new Date(fileLog.documentDate).toLocaleDateString(
                          "nl-BE",
                        )
                      : "-"}
                  </td>

                  {/* Expiration Date with color coding */}
                  <td
                    className={`px-4 py-3 text-xs font-semibold ${
                      isExpired ? "text-brick-primary" : "text-neutral-primary"
                    }`}
                  >
                    {fileLog.expirationDate
                      ? new Date(fileLog.expirationDate).toLocaleDateString(
                          "nl-BE",
                        )
                      : "Onbekend"}
                    {isExpired && <span className="ml-1">⚠️</span>}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      {/* Download button */}
                      <HeouiTooltip content="Download document">
                        <a
                          href={
                            fileLog.downloadUrl || fileLog.sharePointFileUrl
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="border-neutral-secondary-mid text-neutral-primary hover:bg-neutral-secondary-mid rounded-full border bg-transparent p-1.5 transition-colors"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                            />
                          </svg>
                        </a>
                      </HeouiTooltip>

                      {/* Analyze button with Sparkle icon */}
                      <HeouiTooltip
                        content={`Analyseer document ${fileLog.id}`}
                      >
                        <button
                          onClick={() => {
                            onAnalyzeDocument?.(fileLog.id, fileLog.label);
                          }}
                          className="border-moss-primary/30 bg-moss-primary/5 text-moss-primary hover:bg-moss-primary/20 rounded-full border p-1.5 transition-colors"
                        >
                          <Sparkle className="h-4 w-4" weight="fill" />
                        </button>
                      </HeouiTooltip>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-neutral-secondaryMid py-8 text-center">
            Geen document gevonden
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border-moss-primary/30 rounded-lg border bg-white p-4">
      <div className="mb-4 flex items-center gap-2">
        {getChartIcon()}
        <h3 className="text-neutral-primary text-base font-semibold">
          {analyticsData.title}
        </h3>
      </div>

      {(!analyticsData.data ||
        (Array.isArray(analyticsData.data) &&
          analyticsData.data.length === 0)) &&
      analyticsData.type !== "ticket-analysis" ? (
        <div className="text-neutral-secondaryMid py-8 text-center">
          Geen data beschikbaar voor deze periode
        </div>
      ) : (
        <div className="space-y-4">
          {/* For ticket-analysis or chartType='table', show only table without chart */}
          {analyticsData.type === "ticket-analysis" ||
          (analyticsData.chartType === "table" &&
            analyticsData.type !== "filtered-list") ? (
            renderTable()
          ) : (
            <>
              {/* Render chart */}
              {renderChart()}

              {/* Show detailed table below chart */}
              <div className="border-neutral-primary/10 border-t pt-4">
                <h4 className="text-neutral-primary mb-3 text-sm font-semibold">
                  {analyticsData.type === "filtered-list"
                    ? "Alle tickets"
                    : "Gedetailleerde data"}
                </h4>
                {renderTable()}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
